import {
  Text,
  Flex,
  Box,
  BoxProps,
  useDisclosure,
  UnorderedList,
  ListItem,
  TextProps,
} from '@chakra-ui/react';

import { IconInfo } from 'icons';
import {
  RCurrency,
  RModal,
  RTooltip,
  RPositionAndPoints,
  RLoadingAnimation,
} from 'components';
import { useGetPrizePool } from 'hooks';
import { CurrencyType } from 'api/generated/graphql';
import { formatOrdinal } from 'utils/helpers';
import PositionAndPoints from 'components/PositionAndPoints/PositionAndPoints';

export const PrizePoolStrategy = ({
  raceId,
  currency = CurrencyType.Usd,
}: {
  raceId: string;
  currency?: CurrencyType;
}) => {
  const { data: prizePoolData, isLoading: isLoadingPrizePool } =
    useGetPrizePool({ raceId });
  const distributionStrategyData =
    prizePoolData?.getPrizePool.prizePoolDistributions?.sort(
      (a, b) => a.gridPosition - b.gridPosition
    ) || [];

  const returnInterval = (position: number) => {
    if (position === 1) return 'firstPlace';
    if (position >= 2 && position <= 8) return 'payingRange';
    return 'unpaidRange';
  }
  
  return (
    <>
      <Box padding="6">
        <Flex flexDir="column" gap="4" alignItems="center" mb="4">
          <Text
            fontSize={{ base: '0.875rem', md: '1.125rem' }}
            lineHeight={{ base: '1.125rem', md: '1.5rem' }}
            fontWeight={400}
            color="white.80"
          >
            Prizepools display the minimum rewards for 2+ racers, scaling top-heavier with higher levels and more entries—races with fewer than 2 are canceled and tickets refunded.
          </Text>
        </Flex>
        {isLoadingPrizePool && (
          <Flex justifyContent="center">
            <RLoadingAnimation />
          </Flex>
        )}
        {!isLoadingPrizePool &&
          distributionStrategyData &&
          distributionStrategyData.length === 0 && (
            <Text textAlign="center">
              Prize pool information will update when participants enter this
              race.
            </Text>
          )}
        {!isLoadingPrizePool && distributionStrategyData && distributionStrategyData.length > 0 && (
          <Flex
            background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
            borderRadius="8px"
            position="relative"
            p="4"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.12) 100%)',
              borderRadius: '8px',
              padding: '2px',
              mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              maskComposite: 'exclude',
            }}
          >
            <UnorderedList w="full" ml="0">
              {distributionStrategyData.map(
                ({ gridPosition, points, earnings }) => {
                  return (
                    <ListItem
                      display="flex"
                      alignItems="center"
                      mb={
                        gridPosition === 3 
                          ? "3" 
                          : gridPosition === 8 
                            ? "3"
                            : "1"
                      }
                      pr="4"
                      key={gridPosition}
                      gap="4"
                      background={
                        gridPosition === 1 
                        ? "linear-gradient(270deg, rgba(255, 221, 86, 0) 0%, rgba(255, 221, 86, 0.12) 100%)"
                        : gridPosition >= 2 && gridPosition <= 8 
                        ? "linear-gradient(90deg, rgba(79, 197, 186, 0.1) 0%, rgba(79, 197, 186, 0.01) 100%)"
                        : "linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.06) 100%)"
                      }
                      borderRadius="8px"
                    >
                      <Box
                        background={
                          gridPosition === 1 
                          ? "linear-gradient(225deg, #FCF4BF 0%, #EFC862 18%, #CD9832 73%, #BB811B 100%)" 
                          : gridPosition >= 2 && gridPosition <= 8 
                          ? "linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
                          : "linear-gradient(225deg, rgba(255, 255, 255, 0.4) 0%, rgba(238, 238, 238, 0.4) 18%, rgba(160, 160, 160, 0.4) 73%, rgba(151, 151, 151, 0.4) 100%)"
                        }
                        p="2"
                        borderRadius="4px"
                        borderRightRadius="0"
                        minW="40px"
                        textAlign="center"
                      >
                        <Text
                          fontSize="1.25rem"
                          lineHeight="100%"
                          fontWeight={600}
                          color={gridPosition > 8 ? "white.60" : "black.80"}
                          textTransform="uppercase"
                        >
                          {gridPosition}
                        </Text>
                      </Box>

                      <RCurrency
                        amount={earnings}
                        currency={currency}
                        size="md"
                        hasCurrencyCode={false}
                      />

                      <RPositionAndPoints
                        points={points}
                        ml="auto"
                        variant="white"
                      />
                    </ListItem>
                  );
                }
              )}
            </UnorderedList>
          </Flex>
        )}
      </Box>
    </>
  );
};

const PrizePool = ({
  raceId,
  poolAmount = 0,
  text = 'Prize Pool',
  size = 'md',
  currency = CurrencyType.Usd,
  textProps,
  ...rest
}: BoxProps & {
  raceId: string;
  poolAmount: number | undefined;
  text?: string;
  currency?: CurrencyType;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  textProps?: TextProps
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <RTooltip label="View details">
        <Flex
          cursor="pointer"
          flexDir="column"
          gap={0}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          alignItems="flex-end"
          {...rest}
        >
          <Flex flexDir="row" alignItems="center">
            <Text
              fontFamily="heading"
              color="white.80"
              as="h4"
              size={size === 'xl' ? 'lg' : size}
              textTransform="uppercase"
              {...textProps}
            >
              {text}
            </Text>
          </Flex>
          <Flex>
            <RCurrency
              size={size}
              amount={poolAmount}
              currency={currency}
              justifyContent="flex-end"
            />
            <IconInfo ml="2" />
          </Flex>
        </Flex>
      </RTooltip>
      <RModal
        size="3xl"
        title="Prize Pool Placing"
        isOpen={isOpen}
        onClose={onClose}
        variant="darkGrey"
        isCentered
      >
        <PrizePoolStrategy raceId={raceId} currency={currency} {...rest} />
      </RModal>
    </>
  );
};

export default PrizePool;
